import { FC, useCallback, useEffect, useRef, useState } from "react";
import { getVoiceColor, type VoiceOption } from "./voiceSelectShared";
import "./VoiceSelectModal.scss";

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconSearch = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const IconPlay = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IconPause = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const VoiceSelectModal: FC<{
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
  onConfirm: (ids: number[], voices: VoiceOption[]) => void;
  publicVoices: VoiceOption[];
  mineVoices: VoiceOption[];
  mineVoicesLoading?: boolean;
  onMineKeywordChange?: (keyword: string) => void;
  /** 传入后公共音色也走服务端搜索（分页列表用） */
  onPublicKeywordChange?: (keyword: string) => void;
  /** 传入后列表滚到底自动加载下一批 */
  onLoadMore?: (tab: "public" | "mine") => void;
  hasMore?: Partial<Record<"public" | "mine", boolean>>;
  loadingMore?: Partial<Record<"public" | "mine", boolean>>;
  maxCount?: number;
  title?: string;
  subtitle?: string;
  confirmText?: string;
}> = ({
  open,
  onClose,
  selectedIds,
  onConfirm,
  publicVoices,
  mineVoices,
  mineVoicesLoading,
  onMineKeywordChange,
  onPublicKeywordChange,
  onLoadMore,
  hasMore,
  loadingMore,
  maxCount = 3,
  title = "音色选择",
  subtitle,
  confirmText = "确认添加",
}) => {
  const [voiceTab, setVoiceTab] = useState<"public" | "mine">("public");
  const [voiceSearch, setVoiceSearch] = useState("");
  const [draftVoiceIds, setDraftVoiceIds] = useState<number[]>([]);
  const [previewVoiceId, setPreviewVoiceId] = useState<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const stopVoicePreviews = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewVoiceId(null);
  }, []);

  useEffect(() => {
    if (!open) {
      stopVoicePreviews();
      return;
    }
    setDraftVoiceIds([...selectedIds]);
    setVoiceSearch("");
    setVoiceTab("public");
  }, [open, stopVoicePreviews]);

  useEffect(() => {
    if (!open || selectedIds.length === 0) return;
    setDraftVoiceIds((prev) => (prev.length ? prev : [...selectedIds]));
  }, [open, selectedIds]);

  // 切 tab / 改搜索词时列表归顶，避免两个 tab 共用同一个滚动位置
  useEffect(() => {
    if (!open) return;
    const body = bodyRef.current;
    if (body) body.scrollTop = 0;
  }, [open, voiceTab, voiceSearch]);

  useEffect(() => {
    if (!open || voiceTab !== "mine" || !onMineKeywordChange) return;
    const timer = window.setTimeout(() => {
      onMineKeywordChange(voiceSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, voiceSearch, voiceTab, onMineKeywordChange]);

  useEffect(() => {
    if (!open || voiceTab !== "public" || !onPublicKeywordChange) return;
    const timer = window.setTimeout(() => {
      onPublicKeywordChange(voiceSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, voiceSearch, voiceTab, onPublicKeywordChange]);

  // 滚到底部自动加载下一批
  useEffect(() => {
    if (!open || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    const root = bodyRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore(voiceTab);
      },
      { root, rootMargin: "140px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, onLoadMore, voiceTab, publicVoices.length, mineVoices.length, voiceSearch]);

  const handleClose = useCallback(() => {
    stopVoicePreviews();
    onClose();
  }, [onClose, stopVoicePreviews]);

  const toggleDraftVoice = useCallback(
    (id: number) => {
      stopVoicePreviews();
      setDraftVoiceIds((prev) => {
        const idx = prev.indexOf(id);
        if (idx !== -1) return prev.filter((item) => item !== id);
        if (maxCount === 1) return [id];
        if (prev.length >= maxCount) return prev;
        return [...prev, id];
      });
    },
    [maxCount, stopVoicePreviews]
  );

  const visibleVoices = voiceTab === "public" ? publicVoices : mineVoices;
  const filteredVoices = visibleVoices.filter((v) => {
    if (!voiceSearch.trim()) return true;
    const kw = voiceSearch.toLowerCase();
    return v.name.toLowerCase().includes(kw);
  });

  const currentHasMore = voiceTab === "public" ? hasMore?.public : hasMore?.mine;
  const currentLoadingMore = voiceTab === "public" ? loadingMore?.public : loadingMore?.mine;
  const firstLoading =
    voiceTab === "mine"
      ? Boolean(mineVoicesLoading) && mineVoices.length === 0
      : Boolean(loadingMore?.public) && publicVoices.length === 0;

  const handleConfirm = () => {
    const catalog = [...publicVoices, ...mineVoices];
    const voices = draftVoiceIds
      .map((id) => catalog.find((v) => v.id === id))
      .filter((v): v is VoiceOption => Boolean(v));
    stopVoicePreviews();
    onConfirm(draftVoiceIds, voices);
  };

  const defaultSubtitle = `最多选择 ${maxCount} 个音色，脚本中可用 @音色名 指定角色声音`;

  return (
    <div
      className={`voice-ai-modal-overlay${open ? " show" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="voice-ai-modal-content voice-ai-voice-select-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="voice-ai-modal-header">
          <div>
            <h3>{title}</h3>
            <p>{subtitle ?? defaultSubtitle}</p>
          </div>
          <button className="voice-ai-modal-close" onClick={handleClose}>
            <IconX />
          </button>
        </div>
        <div className="voice-ai-modal-tabs">
          {(["public", "mine"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`voice-ai-modal-tab${voiceTab === tab ? " active" : ""}`}
              onClick={() => {
                if (voiceTab === tab) return;
                stopVoicePreviews();
                setVoiceTab(tab);
                setVoiceSearch("");
              }}
            >
              {tab === "public" ? "公共音色" : "我的音色"}
            </button>
          ))}
        </div>
        <div className="voice-ai-modal-search">
          <div className="voice-ai-modal-search-wrap">
            <div className="voice-ai-modal-search-icon">
              <IconSearch />
            </div>
            <input
              type="text"
              placeholder="搜索音色名称"
              value={voiceSearch}
              onChange={(e) => setVoiceSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="voice-ai-modal-body" ref={bodyRef}>
          <div className="voice-ai-voice-grid">
            {firstLoading ? (
              <div className="voice-ai-voice-empty">
                {voiceTab === "public" ? "正在加载公共音色..." : "正在加载我的音色..."}
              </div>
            ) : filteredVoices.length === 0 ? (
              <div className="voice-ai-voice-empty">
                {voiceTab === "public" ? "没有找到匹配音色" : "暂无我的音色"}
              </div>
            ) : (
              filteredVoices.map((v) => {
                const isSelected = draftVoiceIds.includes(v.id);
                const dotClass =
                  v.genderType === "male"
                    ? "voice-ai-voice-gender-dot blue"
                    : v.genderType === "female"
                    ? "voice-ai-voice-gender-dot pink"
                    : "";
                const trimmedDescription = (v.description || "").trim();
                const isPlaceholder = !trimmedDescription;
                return (
                  <div
                    key={`${v.source}-${v.id}`}
                    className={`voice-ai-voice-card${isSelected ? " selected" : ""}`}
                    onClick={() => toggleDraftVoice(v.id)}
                  >
                    <div
                      className="voice-ai-voice-avatar"
                      style={{ background: getVoiceColor(v.id) }}
                    >
                      {v.name.charAt(0)}
                    </div>
                    <div className="voice-ai-voice-info">
                      <div className="voice-ai-voice-title">
                        <span className="voice-ai-voice-name-row">
                          <h4>{v.name}</h4>
                          {dotClass ? <span className={dotClass} /> : null}
                        </span>
                        <span className="voice-ai-voice-check">
                          <IconCheck />
                        </span>
                      </div>
                      <div
                        className={`voice-ai-voice-description${isPlaceholder ? " voice-ai-voice-description-placeholder" : ""}`}
                        title={trimmedDescription || "暂无音色描述"}
                      >
                        {trimmedDescription || "暂无音色描述"}
                      </div>
                    </div>
                    <button
                      className="voice-ai-voice-play-btn"
                      title={previewVoiceId === v.id ? "暂停" : "试听"}
                      disabled={!v.previewAudioUrl}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (previewVoiceId === v.id && previewAudioRef.current) {
                          previewAudioRef.current.pause();
                          previewAudioRef.current = null;
                          setPreviewVoiceId(null);
                        } else if (v.previewAudioUrl) {
                          stopVoicePreviews();
                          const a = new Audio(v.previewAudioUrl);
                          a.onended = () => {
                            setPreviewVoiceId(null);
                            previewAudioRef.current = null;
                          };
                          a.onerror = () => {
                            setPreviewVoiceId(null);
                            previewAudioRef.current = null;
                          };
                          a.play().catch(() => {});
                          previewAudioRef.current = a;
                          setPreviewVoiceId(v.id);
                        }
                      }}
                    >
                      {previewVoiceId === v.id ? <IconPause /> : <IconPlay />}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          {onLoadMore ? (
            <>
              {currentLoadingMore ? (
                <div className="voice-ai-voice-empty voice-ai-voice-more">加载中...</div>
              ) : currentHasMore === false && filteredVoices.length > 0 ? (
                <div className="voice-ai-voice-empty voice-ai-voice-more">没有更多音色了</div>
              ) : null}
              <div ref={sentinelRef} className="voice-ai-voice-sentinel" aria-hidden="true" />
            </>
          ) : null}
        </div>
        <div className="voice-ai-modal-footer">
          <span className="voice-ai-modal-hint">
            已选择 {draftVoiceIds.length} / {maxCount}
          </span>
          <div className="voice-ai-modal-actions">
            <button className="voice-ai-modal-cancel" onClick={handleClose}>
              取消
            </button>
            <button className="voice-ai-modal-confirm" onClick={handleConfirm}>
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
